import React, { useState } from 'react'

import { employersEmployees, employersEmployerSettings } from 'rails_routes'
import { Banner, ButtonLink, Modal } from 'slab'
import { usePostPayrollManageHideAutomatedDeductionsNotice } from 'utils/web_api'

interface PayrollDeductionBannerProps {
  lastEmployeeSync: Date | null
  requiresEmployeeDeduplication: boolean
  automaticDeductionsSetupComplete: boolean
  automaticDeductionsEnabled: boolean
  showAutomatedDeductionsNotice?: boolean
  hideSettingsCta?: boolean
  payrollProviderName?: string
}

type BannerType = 'pre-sync' | 'deduplication' | 'enable-deductions'

interface BannerConfig {
  type: 'info' | 'notice' | 'warning'
  title: string
  body: string
  cta?: {
    text: string
    href: string
  }
  bannerType: BannerType
}

const PayrollDeductionBannerImpl: React.FC<PayrollDeductionBannerProps> = ({
  lastEmployeeSync,
  requiresEmployeeDeduplication,
  automaticDeductionsSetupComplete,
  automaticDeductionsEnabled,
  showAutomatedDeductionsNotice = false,
  hideSettingsCta = false,
  payrollProviderName,
}) => {
  const [isHidden, setIsHidden] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const hideNoticeMutation = usePostPayrollManageHideAutomatedDeductionsNotice({
    mutation: {
      onSuccess: (data) => {
        if (data && !data.show_automated_deductions_notice) {
          setIsHidden(true)
          setShowConfirmModal(false)
        }
      },
    },
    request: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })

  const getBannerConfig = (): BannerConfig | null => {
    // If the banner has been manually hidden, don't show it
    if (isHidden) {
      return null
    }

    // Banner 1: Pre-sync notice (no initial sync completed yet)
    if (!lastEmployeeSync) {
      return {
        type: 'info',
        title: 'Automated payroll deductions will be available after your first employee sync',
        body: `Your employees are syncing from ${payrollProviderName || 'your payroll provider'}. You can enable automated deductions if no conflicts are detected with your currently invited employees. If conflicts are found, you'll be asked to resolve them first.`,
        bannerType: 'pre-sync'
      }
    }

    // Banner 2: Deduplication required (highest priority)
    if (requiresEmployeeDeduplication) {
      return {
        type: 'warning',
        title: 'Automated payroll deductions are not available',
        body: 'You have invited employees that could not be matched to the employees in your payroll system. These conflicts must be resolved before you can enable automated payroll deductions.',
        cta: {
          text: 'Resolve conflicts',
          href: employersEmployees.duplicates()
        },
        bannerType: 'deduplication'
      }
    }

    // Banner 3: Ready to enable deductions (only show if showAutomatedDeductionsNotice is true)
    if (automaticDeductionsSetupComplete && !automaticDeductionsEnabled && showAutomatedDeductionsNotice) {
      return {
        type: 'notice',
        title: 'Ready to enable automated payroll deductions',
        body: 'Your payroll integration is set up and no employee conflicts were detected. You can now enable automated payroll deductions.',
        cta: hideSettingsCta
          ? undefined
          : {
              text: 'Go to payroll settings',
              href: employersEmployerSettings.show({ query: { tab: 'payroll' } })
            },
        bannerType: 'enable-deductions'
      }
    }

    return null
  }

  const bannerConfig = getBannerConfig()

  if (!bannerConfig) {
    return null
  }

  const handleDismiss = () => {
    if (bannerConfig.bannerType === 'enable-deductions') {
      setShowConfirmModal(true)
    }
  }

  const handleConfirmDismiss = () => {
    hideNoticeMutation.mutate()
  }

  const renderCta = () => {
    if (!bannerConfig.cta) return null

    return (
      <ButtonLink
        variant='secondary'
        href={bannerConfig.cta.href}
      >
        {bannerConfig.cta.text}
      </ButtonLink>
    )
  }

  return (
    <div className='mb-4'>
      <Banner
        type={bannerConfig.type}
        title={bannerConfig.title}
        cta={renderCta()}
        onDismiss={bannerConfig.bannerType === 'enable-deductions' ? handleDismiss : undefined}
      >
        {bannerConfig.body}
      </Banner>

      <Modal open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <Modal.Body>
          <Modal.Title>Hide automation reminders</Modal.Title>
          <Modal.Description>
            We won't remind you about this again. You can still enable payroll automation in your payroll settings.
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Button variant='primary' onClick={handleConfirmDismiss}>
            Confirm
          </Modal.Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

const PayrollDeductionBanner = (props: PayrollDeductionBannerProps) => (
  <PayrollDeductionBannerImpl {...props} />
)

export default PayrollDeductionBanner
